import { Button, Flex, Text, Tabs, TabList } from "@chakra-ui/react";
import { CSSProperties, useMemo } from "react";
import { BrowseTabType } from "../../../types";
import { UnderlinedTab } from "../../../components/tabs/UnderlinedTab";
import type { SearchV2Counts } from "../hooks/useSearchV2API";

interface BrowseTabsProps {
  setActive: (index: BrowseTabType) => void;
  active: BrowseTabType;
  counts?: SearchV2Counts;
}

type VisibleTab = {
  type: BrowseTabType;
  label: string;
  count?: number;
};

function useVisibleTabs(counts: SearchV2Counts | undefined): VisibleTab[] {
  return useMemo(() => [
    { type: BrowseTabType.comics, label: "Series", count: counts?.series },
    { type: BrowseTabType.releases, label: "Releases", count: counts?.releases },
    { type: BrowseTabType.creators, label: "Creators", count: counts?.creators },
    { type: BrowseTabType.bundles, label: "Bundles", count: counts?.bundles },
  ], [counts]);
}

export function FloatingBrowseTab({ setActive, active }: BrowseTabsProps) {
  const visibleTabs = useVisibleTabs(undefined);

  return (
    <Flex
      borderRadius="20px"
      background="charcoal.charcoal"
      backdropFilter="blur(18px)"
      border="1px solid"
      borderColor="transparent.white.10"
      boxShadow="0px 4px 24px 0px rgba(0, 0, 0, 0.40)"
      width={visibleTabs.length >= 4 ? "330px" : "265px"}
      height="48px"
      justifyContent="space-evenly"
      alignItems="center"
    >
      {visibleTabs.map((tab) => (
        <BrowseTab
          key={tab.type}
          text={tab.label}
          onClick={() => setActive(tab.type)}
          isActive={active === tab.type}
          floating
          fontSize="16px"
          fontWeight={700}
          fontFamily="Bricolage Grotesque"
        />
      ))}
    </Flex>
  );
}

function formatCount(count: number): string {
  if (count >= 1000) return "1k+";
  return String(count);
}

export function BrowseTabs({ setActive, active, counts }: BrowseTabsProps) {
  const visibleTabs = useVisibleTabs(counts);

  // Map between visual index and BrowseTabType
  const visualIndex = visibleTabs.findIndex((t) => t.type === active);

  return (
    <Tabs
      variant="unstyled"
      colorScheme="blaze"
      index={visualIndex >= 0 ? visualIndex : 0}
      onChange={(index) => {
        const tab = visibleTabs[index];
        if (tab) setActive(tab.type);
      }}
    >
      <TabList>
        {visibleTabs.map((tab) => (
          <UnderlinedTab key={tab.type} whiteSpace="nowrap">
            {tab.count !== undefined && tab.count > 0
              ? `${tab.label} (${formatCount(tab.count)})`
              : tab.label}
          </UnderlinedTab>
        ))}
      </TabList>
    </Tabs>
  );
}

interface BrowseTabProps {
  text: string;
  isActive: boolean;
  floating?: boolean;
  onClick: () => void;
  fontSize?: CSSProperties["fontSize"];
  fontWeight?: CSSProperties["fontWeight"];
  fontFamily?: CSSProperties["fontFamily"];
}
function BrowseTab({
  text,
  isActive,
  onClick,
  floating,
  fontSize,
  fontWeight,
  fontFamily,
}: BrowseTabProps) {
  return (
    <Button
      variant={floating ? "browseTab" : undefined}
      key={text}
      transition="all .2s linear"
      cursor="pointer"
      onClick={onClick}
      // px="36px"
      height="40px"
      alignItems="center"
      justifyContent="center"
      borderRadius="16px"
      bg={
        floating ? undefined : isActive ? "blaze.600" : "transparent.white.10"
      }
      color={"neutral.white"}
      _hover={{
        color: isActive ? "neutral.white" : "dune.100",
        background: isActive ? undefined : "transparent.white.20",
      }}
      marginInline="4px"
    >
      <Text
        transition="all .2s linear"
        fontFamily={fontFamily}
        fontSize={fontSize ?? "14px"}
        fontWeight={fontWeight}
        lineHeight="130%"
        color={floating ? (isActive ? "dune.100" : "dune.700") : "white"}
        _groupHover={{ color: "dune.100" }}
        marginBottom="2px"
      >
        {text}
      </Text>
    </Button>
  );
}
